import isEmpty from 'lodash/isEmpty';
import { clearCurrentUser, fetchCurrentUser } from './user.duck';
import { createUserWithIdp, post } from '../util/api';
import { storableError } from '../util/errors';
import * as log from '../util/log';
import { ListingCategory, ListingType, TransactionProcessAlias, UnitType } from '../util/enums';
import moment from 'moment';

const authenticated = authInfo => authInfo && authInfo.isAnonymous === false;

// ================ Action types ================ //

export const AUTH_INFO_REQUEST = 'app/auth/AUTH_INFO_REQUEST';
export const AUTH_INFO_SUCCESS = 'app/auth/AUTH_INFO_SUCCESS';

export const LOGIN_REQUEST = 'app/auth/LOGIN_REQUEST';
export const LOGIN_SUCCESS = 'app/auth/LOGIN_SUCCESS';
export const LOGIN_ERROR = 'app/auth/LOGIN_ERROR';

export const LOGOUT_REQUEST = 'app/auth/LOGOUT_REQUEST';
export const LOGOUT_SUCCESS = 'app/auth/LOGOUT_SUCCESS';
export const LOGOUT_ERROR = 'app/auth/LOGOUT_ERROR';

export const SIGNUP_REQUEST = 'app/auth/SIGNUP_REQUEST';
export const SIGNUP_SUCCESS = 'app/auth/SIGNUP_SUCCESS';
export const SIGNUP_ERROR = 'app/auth/SIGNUP_ERROR';

export const CONFIRM_REQUEST = 'app/auth/CONFIRM_REQUEST';
export const CONFIRM_SUCCESS = 'app/auth/CONFIRM_SUCCESS';
export const CONFIRM_ERROR = 'app/auth/CONFIRM_ERROR';

// Generic user_logout action that can be handled elsewhere
// E.g. src/reducers.js clears store as a consequence
export const USER_LOGOUT = 'app/USER_LOGOUT';

// ================ Reducer ================ //

const initialState = {
  isAuthenticated: false,

  // scopes associated with current token
  authScopes: [],

  // auth info
  authInfoLoaded: false,

  // login
  loginError: null,
  loginInProgress: false,

  // logout
  logoutError: null,
  logoutInProgress: false,

  // signup
  signupError: null,
  signupInProgress: false,

  // confirm (create use with idp)
  confirmError: null,
  confirmInProgress: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case AUTH_INFO_REQUEST:
      return state;
    case AUTH_INFO_SUCCESS:
      return {
        ...state,
        authInfoLoaded: true,
        isAuthenticated: authenticated(payload),
        authScopes: payload.scopes,
      };

    case LOGIN_REQUEST:
      return {
        ...state,
        loginInProgress: true,
        loginError: null,
        logoutError: null,
        signupError: null,
      };
    case LOGIN_SUCCESS:
      return { ...state, loginInProgress: false, isAuthenticated: true };
    case LOGIN_ERROR:
      return { ...state, loginInProgress: false, loginError: payload };

    case LOGOUT_REQUEST:
      return { ...state, logoutInProgress: true, loginError: null, logoutError: null };
    case LOGOUT_SUCCESS:
      return { ...state, logoutInProgress: false, isAuthenticated: false, authScopes: [] };
    case LOGOUT_ERROR:
      return { ...state, logoutInProgress: false, logoutError: payload };

    case SIGNUP_REQUEST:
      return { ...state, signupInProgress: true, loginError: null, signupError: null };
    case SIGNUP_SUCCESS:
      return { ...state, signupInProgress: false };
    case SIGNUP_ERROR:
      return { ...state, signupInProgress: false, signupError: payload };

    case CONFIRM_REQUEST:
      return { ...state, confirmInProgress: true, loginError: null, confirmError: null };
    case CONFIRM_SUCCESS:
      return { ...state, confirmInProgress: false, isAuthenticated: true };
    case CONFIRM_ERROR:
      return { ...state, confirmInProgress: false, confirmError: payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const authenticationInProgress = state => {
  const { loginInProgress, logoutInProgress, signupInProgress } = state.auth;
  return loginInProgress || logoutInProgress || signupInProgress;
};

export const isAuthenticatedSelector = state => state.auth.isAuthenticated;
// ================ Action creators ================ //

export const authInfoRequest = () => ({ type: AUTH_INFO_REQUEST });
export const authInfoSuccess = info => ({ type: AUTH_INFO_SUCCESS, payload: info });

export const loginRequest = () => ({ type: LOGIN_REQUEST });
export const loginSuccess = () => ({ type: LOGIN_SUCCESS });
export const loginError = error => ({ type: LOGIN_ERROR, payload: error, error: true });

export const logoutRequest = () => ({ type: LOGOUT_REQUEST });
export const logoutSuccess = () => ({ type: LOGOUT_SUCCESS });
export const logoutError = error => ({ type: LOGOUT_ERROR, payload: error, error: true });

export const signupRequest = () => ({ type: SIGNUP_REQUEST });
export const signupSuccess = () => ({ type: SIGNUP_SUCCESS });
export const signupError = error => ({ type: SIGNUP_ERROR, payload: error, error: true });

export const confirmRequest = () => ({ type: CONFIRM_REQUEST });
export const confirmSuccess = () => ({ type: CONFIRM_SUCCESS });
export const confirmError = error => ({ type: CONFIRM_ERROR, payload: error, error: true });

export const userLogout = () => ({ type: USER_LOGOUT });

// ================ Thunks ================ //

export const authInfo = () => (dispatch, getState, sdk) => {
  dispatch(authInfoRequest());
  return sdk
    .authInfo()
    .then(info => dispatch(authInfoSuccess(info)))
    .catch(e => {
      // Requesting auth info just reads the token from the token
      // store (i.e. cookies), and should not fail in normal
      // circumstances. If it fails, it's due to a programming
      // error. In that case we mark the operation done and dispatch
      // `null` success action that marks the user as unauthenticated.
      log.error(e, 'auth-info-failed');
      dispatch(authInfoSuccess(null));
    });
};

export const login = (username, password) => async (dispatch, getState, sdk) => {
  // guard against concurrent auth
  if (authenticationInProgress(getState())) {
    throw new Error('Login or logout already in progress');
  }

  dispatch(loginRequest());

  try {
    // Normalize email (username) to lowercase for case-insensitive comparison
    const normalizedUsername = username.toLowerCase();
    await sdk.login({ username: normalizedUsername, password });
    dispatch(loginSuccess());
    // return this promise so callers know when currentUser is fetched
    return dispatch(fetchCurrentUser());
  } catch (rawError) {
    const error = storableError(rawError);
    dispatch(loginError(error));
    throw error;
  }
};

export const logout = () => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(logoutRequest());

  // Note that the thunk does not reject when the logout fails, it
  // just dispatches the logout error action.
  return sdk
    .logout()
    .then(() => {
      // The order of the dispatched actions
      dispatch(logoutSuccess());
      dispatch(clearCurrentUser());
      log.clearUserId();
      dispatch(userLogout());
      localStorage.removeItem('idlePromptShown');
    })
    .catch(e => dispatch(logoutError(storableError(e))));
};

export const signup = (params, listingParams) => async (dispatch, getState, sdk) => {
  // Prevent signups if another authentication process is in progress
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(signupRequest());

  // Destructure values from params for easier access
  // NOTE: isSupplierSignup and isCustomerSignup should go into publicData for post-verification redirect
  const {
    email,
    password,
    firstName,
    lastName,
    consentToShareContactInfo,
    isDreamHomePage,
    isFirstStepCompleted,
    ...rest
  } = params;

  // Normalize email to lowercase for case-insensitive comparison
  const normalizedEmail = email.toLowerCase();

  // Create the signup parameters.
  // When there is no additional data, we keep it minimal.
  // Otherwise, include optional data conditionally.
  const createUserParams = isEmpty(rest)
    ? { email: normalizedEmail, password, firstName, lastName }
    : {
        email: normalizedEmail,
        password,
        firstName,
        lastName,
        ...(consentToShareContactInfo ? { protectedData: { consentToShareContactInfo } } : {}),
        // Merge optional private data so that both keys can coexist
        ...(isDreamHomePage
          ? {
              privateData: {
                ...(isDreamHomePage ? { password } : {}),
              },
            }
          : {}),
        publicData: { ...rest },
      };

  // Attempt to create the user, then dispatch success and login actions.
  try {
    await sdk.currentUser.create(createUserParams);
    dispatch(signupSuccess());
    // We login the user automatically after successful signup.
    await dispatch(login(normalizedEmail, password));

    // Register customer in LeadFlow if this is a customer signup (selfBuilder / propertyDeveloper)
    const isCustomerUserType = rest.userType === 'selfBuilder' || rest.userType === 'propertyDeveloper';
    if (isCustomerUserType) {
      try {
        const meResponse = await sdk.currentUser.show();
        const sharetribeUserId = meResponse?.data?.data?.id?.uuid;
        if (sharetribeUserId) {
          const registerResponse = await post(
            '/api/customers/register',
            JSON.stringify({
              sharetribeUserId,
              firstName,
              lastName,
              email: normalizedEmail,
              planningRef: rest.planningReference || undefined,
            }),
            { headers: { 'Content-Type': 'application/json' } }
          );
          // Store match result so the homepage can show a dismissible popup
          if (rest.planningReference) {
            try {
              sessionStorage.setItem('leadflow_match_result', JSON.stringify({
                matched: !!registerResponse?.matched,
                planningRef: rest.planningReference,
              }));
            } catch (e) {}
          }
        }
      } catch (leadflowErr) {
        console.error('[auth.duck] LeadFlow customer registration failed:', leadflowErr);
      }
    }

    if (isFirstStepCompleted) {
      const { plotAddress, moveInDate, completionDate, onsiteStartDate } = listingParams || {};
      const selectedPlace = plotAddress?.selectedPlace || {};
      const address = selectedPlace?.address || '';
      const origin = selectedPlace?.origin || null;
      const title = `${firstName} ${lastName}`;
      const publicData = {
        ...listingParams,
        ...(address && { plotAddress: address }),
        ...(completionDate?.date && { completionDate: moment(completionDate.date).unix() }),
        ...(moveInDate?.date && { moveInDate: moment(moveInDate.date).unix() }),
        ...(onsiteStartDate?.date && { onsiteStartDate: moment(onsiteStartDate.date).unix() }),
        transactionProcessAlias: TransactionProcessAlias.DEFAULT_INQUIRY,
        unitType: UnitType.INQUIRY,
        isFinalStepCompleted: false,
        listingType: ListingType.FREE_FLOW,
        listingCategory: ListingCategory.DREAM_HOME,
      };
      const updatedValues = {
        title,
        ...(origin && { geolocation: origin }),
        publicData,
      };
      await sdk.ownListings.create(updatedValues);
    }
  } catch (e) {
    dispatch(signupError(storableError(e)));
    log.error(e, 'signup-failed', { email, firstName, lastName });
  }
};

export const signupWithIdp = (params, listingParams) => async (dispatch, getState, sdk) => {
  const { isFirstStepCompleted, plotAddress, moveInDate, onsiteStartDate, ...rest } =
    listingParams || {};
  try {
    dispatch(confirmRequest());
    await createUserWithIdp(params);
    await dispatch(confirmSuccess());
    await dispatch(fetchCurrentUser());
    if (isFirstStepCompleted) {
      const selectedPlace = plotAddress?.selectedPlace || {};
      const address = selectedPlace?.address || '';
      const origin = selectedPlace?.origin || null;
      const title = `${params.firstName} ${params.lastName}`;
      const publicData = {
        ...rest,
        ...(address && { plotAddress: address }),
        ...(moveInDate?.date && { moveInDate: moment(moveInDate.date).unix() }),
        ...(onsiteStartDate?.date && { onsiteStartDate: moment(onsiteStartDate.date).unix() }),
        transactionProcessAlias: TransactionProcessAlias.DEFAULT_INQUIRY,
        unitType: UnitType.INQUIRY,
        isFinalStepCompleted: false,
        listingType: ListingType.FREE_FLOW,
        listingCategory: ListingCategory.DREAM_HOME,
      };
      const updatedValues = {
        title,
        ...(origin && { geolocation: origin }),
        publicData,
      };
      await sdk.ownListings.create(updatedValues);
    }
  } catch (e) {
    log.error(e, 'create-user-with-idp-failed', { params });
    return dispatch(confirmError(storableError(e)));
  }
};
